import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Typography, Button, Descriptions, Tag, Spin,
    Space, Badge, Empty, message, Modal, Form, Input, DatePicker,
    Select, InputNumber, Timeline, Table, Tooltip, Alert, Statistic,
    Divider, Collapse, Tabs, Popconfirm
} from 'antd';
import {
    DollarOutlined, EyeOutlined, PlusOutlined, DownloadOutlined,
    HistoryOutlined, CreditCardOutlined, TransactionOutlined,
    FileTextOutlined, SendOutlined, BankOutlined,
    CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
    CopyOutlined, CalculatorOutlined, PercentageOutlined,
    ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined,
    EditOutlined, SaveOutlined, UserOutlined
} from '@ant-design/icons';
import API from '../../../../API/API';
import dayjs from 'dayjs';
import { formatPSTDate } from '../../utils/dateUtils';
import { getActorName, getActorId } from '../../utils/actorUtils';
import { getTotalAmountChange } from '../../utils/quoteHistoryUtils';
import { roundMoney, getBalance } from '../../../../utils/money';
import TabPane from 'antd/es/tabs/TabPane';

const { Title, Text } = Typography;
const { Option } = Select;

// Helper functions
const calculateTotalPaid = (leadData) => {
    if (!leadData?.advancePayments) return 0;
    return roundMoney(leadData.advancePayments.reduce((sum, payment) => {
        if (payment.status === 'Completed' || payment.status === undefined) {
            return sum + (Number(payment.amount) || 0);
        }
        return sum;
    }, 0));
};

const calculateBalance = (leadData) => getBalance(leadData?.totalAmount, calculateTotalPaid(leadData));

// Payment History order: newest payment date first; for the same date, the one
// recorded later comes first. Each entry keeps its index in lead.advancePayments,
// so "Transaction #" numbering doesn't change with the display order.
const sortPaymentsNewestFirst = (payments = []) => {
    const toTime = (date) => {
        const time = new Date(date || 0).getTime();
        return Number.isFinite(time) ? time : 0;
    };

    return payments
        .map((payment, index) => ({ payment, index }))
        .sort((a, b) => (toTime(b.payment.date) - toTime(a.payment.date)) || (b.index - a.index));
};

const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
        case 'card': return <CreditCardOutlined />;
        case 'payment link': return <SendOutlined />;
        case 'check': case 'check payment': return <FileTextOutlined />;
        case 'zelle': return <TransactionOutlined />;
        case 'cashapp': case 'venmo': case 'paypal': return <DollarOutlined />;
        case 'wire transfer': return <TransactionOutlined />;
        case 'bank transfer': return <BankOutlined />;
        case 'cash': return <DollarOutlined />;
        default: return <DollarOutlined />;
    }
};

const getPaymentMethodColor = (method) => {
    switch (method?.toLowerCase()) {
        case 'card': return 'blue';
        case 'payment link': return 'purple';
        case 'check': case 'check payment': return 'orange';
        case 'zelle': return 'green';
        case 'cashapp': return 'green';
        case 'venmo': return '#3d95ce';
        case 'paypal': return '#003087';
        case 'wire transfer': return 'cyan';
        case 'bank transfer': return 'geekblue';
        case 'cash': return 'gold';
        default: return 'default';
    }
};

const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'completed': return 'green';
        case 'pending': return 'orange';
        case 'failed': return 'red';
        case 'refunded': return 'purple';
        case 'cancelled': return 'gray';
        case 'processing': return 'blue';
        default: return 'default';
    }
};

const PaymentDetailTab = ({ lead, agentMap, onUpdateSuccess, onOpenEditDrawer }) => {
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activePaymentTab, setActivePaymentTab] = useState('summary');
    const [isAdmin, setIsAdmin] = useState(false);
    const [payementAllow, setpayementAllow] = useState('No');
    
    // Edit payment modal states
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [editForm] = Form.useForm();
    const [editLoading, setEditLoading] = useState(false);
const [isTotalAmountModalVisible, setIsTotalAmountModalVisible] = useState(false);
const [editingTotalAmount, setEditingTotalAmount] = useState(0);
const [savingTotalAmount, setSavingTotalAmount] = useState(false);
const isEditTotalAmountDisabled = lead?.leadStage === 'Sales Made' && !isAdmin;
    // Quote entry behind the current total: who changed it and when.
    const totalAmountChange = getTotalAmountChange(lead);
    const totalAmountChangedBy = totalAmountChange
        ? getActorName(totalAmountChange.changedBy, agentMap) || 'Unknown'
        : null;
    useEffect(() => {
        const userData = localStorage.getItem('userData');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setIsAdmin(user.role === 'ADMIN');
            } catch (e) {
                console.error('Error parsing user data:', e);
                setIsAdmin(false);
            }
        }
    }, []);

    useEffect(() => {
        if (lead) {
            loadPaymentDetails();
            if (lead.payementAllow) {
                setpayementAllow(lead.payementAllow);
            }
        }
    }, [lead]);

const handleEditTotalAmount = () => {
    setEditingTotalAmount(paymentDetails?.totalAmount || 0);
    setIsTotalAmountModalVisible(true);
};

const handleSaveTotalAmount = async () => {
    if (!editingTotalAmount || editingTotalAmount <= 0) {
        message.error('Please enter a valid amount');
        return;
    }
    
    setSavingTotalAmount(true);
    try {
        const response = await API.UpdateLead(lead._id, { 
            totalAmount: Number(editingTotalAmount)
        });
        
        if (response?.status || response?.success) {
            message.success('Total amount updated successfully');
            loadPaymentDetails();
            if (onUpdateSuccess) onUpdateSuccess();
            setIsTotalAmountModalVisible(false);
        } else {
            message.error(response?.message || 'Failed to update total amount');
        }
    } catch (error) {
        console.error('Error updating total amount:', error);
        message.error('Failed to update total amount');
    } finally {
        setSavingTotalAmount(false);
    }
};
    const loadPaymentDetails = async () => {
        try {
            setLoading(true);
            const payments = lead.advancePayments || [];
            const totalAmount = lead.totalAmount || 0;
            const paidAmount = calculateTotalPaid(lead);
            const balanceDue = calculateBalance(lead);

            const completedPayments = payments.filter(p => p.status === 'Completed');
            const pendingPayments = payments.filter(p => p.status === 'Pending');
            const failedPayments = payments.filter(p => p.status === 'Failed');

            const paymentMethods = {};
            payments.forEach(payment => {
                const method = payment.paymentMethod || 'Unknown';
                paymentMethods[method] = (paymentMethods[method] || 0) + 1;
            });

            const monthlyStats = {};
            payments.forEach(payment => {
                const month = dayjs(payment.date).format('MMM YYYY');
                if (!monthlyStats[month]) {
                    monthlyStats[month] = { count: 0, amount: 0 };
                }
                monthlyStats[month].count++;
                monthlyStats[month].amount += Number(payment.amount);
            });

            setPaymentDetails({
                totalAmount,
                paidAmount,
                balanceDue,
                payments,
                currency: lead.currency || 'USD',
                paymentTerms: lead.paymentTerms || 'Net 30',
                invoiceNumber: lead.invoiceNumber || `INV-${lead.orderNumber || lead._id.slice(-8).toUpperCase()}`,
                lastPaymentDate: payments.length > 0 ? payments[payments.length - 1].date : null,
                paymentCount: payments.length,
                completedCount: completedPayments.length,
                pendingCount: pendingPayments.length,
                failedCount: failedPayments.length,
                paymentMethods,
                monthlyStats,
                averagePayment: completedPayments.length > 0 ?
                    completedPayments.reduce((sum, p) => sum + Number(p.amount), 0) / completedPayments.length : 0,
                hasTotalAmount: totalAmount > 0,
                isFullyPaid: balanceDue <= 0,
            });
        } catch (error) {
            console.error('Error loading payment details:', error);
            message.error('Failed to load payment details');
        } finally {
            setLoading(false);
        }
    };

const handleEditPayment = (payment, index) => {
    setEditingPayment({ ...payment, index });
    
    // Create a clean object with properly formatted dates
    const formattedPayment = {
        amount: payment.amount,
        paymentMethod: payment.paymentMethod,
        status: payment.status,
        description: payment.description || '',
        notes: payment.notes || '',
        date: payment.date ? dayjs(payment.date) : dayjs(),
        transactionId: payment.transactionId,
        cardType: payment.cardType,
        cardHolderName: payment.cardHolderName,
        // Never prefill cardNumber or cvv — those are never persisted/displayed
        checkNumber: payment.checkNumber,
        expirationDate: payment.expirationDate ? dayjs(payment.expirationDate) : null,
        checkDate: payment.checkDate ? dayjs(payment.checkDate) : null,
        promiseToPayDate: payment.promiseToPayDate ? dayjs(payment.promiseToPayDate) : null,
        linkExpiryDate: payment.linkExpiryDate ? dayjs(payment.linkExpiryDate) : null,
    };
    
    // Only include fields that exist in the original payment
    Object.keys(formattedPayment).forEach(key => {
        if (formattedPayment[key] === undefined) {
            delete formattedPayment[key];
        }
    });
    
    editForm.setFieldsValue(formattedPayment);
    setEditModalVisible(true);
};

   const handleSaveEditedPayment = async (values) => {
    try {
        setEditLoading(true);
        
        console.log("Starting payment update...");
        console.log("Lead ID:", lead._id);
        console.log("Editing payment index:", editingPayment.index);
        console.log("Form values:", values);
        
        const currentPayments = [...(lead.advancePayments || [])];
        const paymentIndex = editingPayment.index;
        
        const updatedPayment = {
            ...currentPayments[paymentIndex],
            ...values,
            date: values.date ? values.date.toISOString() : new Date().toISOString(),
            amount: Number(values.amount),
            lastUpdatedBy: JSON.parse(localStorage.getItem('userData'))?._id,
            lastUpdatedAt: new Date().toISOString()
        };
        
        console.log("Updated payment object:", updatedPayment);
        
        currentPayments[paymentIndex] = updatedPayment;
        
        console.log("Full payments array to save:", currentPayments);
        
        // Make the API call
        const response = await API.UpdateLead(lead._id, {
            advancePayments: currentPayments,
            replaceAllPayments: true
        });
        
        console.log("API Response:", response);
        
        if (response?.status || response?.success) {
            message.success('Payment updated successfully');
            setEditModalVisible(false);
            editForm.resetFields();
            loadPaymentDetails();
            if (onUpdateSuccess) {
                onUpdateSuccess();
            }
        } else {
            console.error("API returned error:", response);
            message.error(response?.message || 'Failed to update payment');
        }
    } catch (error) {
        console.error('Error updating payment:', error);
        message.error('Failed to update payment: ' + (error.message || 'Unknown error'));
    } finally {
        setEditLoading(false);
    }
};

    const handleAddPayment = () => {
        if (onOpenEditDrawer) {
            onOpenEditDrawer();
        }
    };

    // Who added a payment and, if someone else edited it later, who did.
    // lastUpdatedBy is also stamped when a payment is created, so it only
    // counts as an edit when it names a different user.
    const getPaymentActors = (payment) => {
        const addedBy = getActorName(payment.createdBy, agentMap) || 'Unknown';
        const updatedById = getActorId(payment.lastUpdatedBy);
        const updatedBy = updatedById && updatedById !== getActorId(payment.createdBy)
            ? getActorName(payment.lastUpdatedBy, agentMap) || 'Unknown'
            : null;
        return { addedBy, updatedBy };
    };

    const renderPaymentActors = (payment) => {
        const { addedBy, updatedBy } = getPaymentActors(payment);
        return (
            <div style={{ marginBottom: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    <UserOutlined /> Added by <Text strong style={{ fontSize: '12px' }}>{addedBy}</Text>
                    {updatedBy && (
                        <> • Updated by <Text strong style={{ fontSize: '12px' }}>{updatedBy}</Text></>
                    )}
                </Text>
            </div>
        );
    };

    const showPaymentDetailsModal = (payment, index) => {
        const { addedBy, updatedBy } = getPaymentActors(payment);
        Modal.info({
            title: `Payment Details - Transaction #${index + 1}`,
            width: 1000,
            content: (
                <div style={{ marginTop: '20px' }}>
                    <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Transaction Date" span={2}>
                            <Text strong>{formatPSTDate(payment.date)}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Amount">
                            <Text strong style={{ color: '#52c41a', fontSize: '24px' }}>
                                ${Number(payment.amount).toFixed(2)}
                            </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Badge
                                status={payment.status === 'Completed' ? 'success' :
                                    payment.status === 'Pending' ? 'processing' : 'error'}
                                text={
                                    <Tag color={getPaymentStatusColor(payment.status)}>
                                        {payment.status}
                                    </Tag>
                                }
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="Payment Method">
                            <Tag
                                color={getPaymentMethodColor(payment.paymentMethod)}
                                icon={getPaymentMethodIcon(payment.paymentMethod)}
                                style={{ fontSize: '14px', padding: '6px 12px' }}
                            >
                                {payment.paymentMethod}
                            </Tag>
                        </Descriptions.Item>

                        {payment.transactionId && (
                            <Descriptions.Item label="Transaction ID" span={2}>
                                <Text copyable style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                                    {payment.transactionId}
                                </Text>
                            </Descriptions.Item>
                        )}

                        {payment.cardType && (
                            <Descriptions.Item label="Card Type">
                                <Tag>{payment.cardType}</Tag>
                            </Descriptions.Item>
                        )}

                        {/* Never display the full card number or CVV — masked last-4 only */}
                        {payment.lastFourDigits && (
                            <Descriptions.Item label="Card Number">
                                <Text style={{ fontFamily: 'monospace' }}>
                                   •••• {payment.lastFourDigits}
                                </Text>
                            </Descriptions.Item>
                        )}
                        {payment.expirationDate && (
                            <Descriptions.Item label="Expiration Date">
                                <Text style={{ fontFamily: 'monospace' }}>
                                   {payment.expirationDate}
                                </Text>
                            </Descriptions.Item>
                        )}
                        {payment.cardHolderName && (
                            <Descriptions.Item label="Card Holder Name">
                                <Text>
                                   {payment.cardHolderName}
                                </Text>
                            </Descriptions.Item>
                        )}
                        {payment.checkNumber && (
                            <Descriptions.Item label="Check Number">
                                <Text>{payment.checkNumber}</Text>
                            </Descriptions.Item>
                        )}

                        {payment.description && (
                            <Descriptions.Item label="Description" span={2}>
                                <Card size="small" style={{ backgroundColor: '#fafafa', marginTop: '8px' }}>
                                    {payment.description}
                                </Card>
                            </Descriptions.Item>
                        )}

                        {payment.notes && (
                            <Descriptions.Item label="Notes" span={2}>
                                <Card size="small" style={{ backgroundColor: '#f6ffed', marginTop: '8px' }}>
                                    {payment.notes}
                                </Card>
                            </Descriptions.Item>
                        )}

                        <Descriptions.Item label="Added By">
                            {addedBy}
                        </Descriptions.Item>

                        <Descriptions.Item label="Processed At">
                            {payment.createdAt ? formatPSTDate(payment.createdAt) : formatPSTDate(payment.date)}
                        </Descriptions.Item>

                        {updatedBy && (
                            <Descriptions.Item label="Last Updated By" span={2}>
                                {updatedBy}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </div>
            ),
            okText: 'Close',
        });
    };

  const renderEditPaymentModal = () => {
    if (!editingPayment) return null;
    
    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <EditOutlined />
                    <span>Edit Payment</span>
                </div>
            }
            open={editModalVisible}
            onCancel={() => {
                setEditModalVisible(false);
                editForm.resetFields();
                setEditingPayment(null);
            }}
            footer={[
                <Button key="cancel" onClick={() => {
                    setEditModalVisible(false);
                    editForm.resetFields();
                    setEditingPayment(null);
                }}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={() => editForm.submit()}
                    loading={editLoading}
                    icon={<SaveOutlined />}
                >
                    Save Changes
                </Button>
            ]}
            width={700}
        >
            <Form
                form={editForm}
                layout="vertical"
                onFinish={handleSaveEditedPayment}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="amount"
                            label="Amount"
                            rules={[{ required: true, message: 'Amount is required' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                prefix="$"
                                min={0.01}
                                step={0.01}
                                precision={2}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="date"
                            label="Payment Date"
                            rules={[{ required: true, message: 'Date is required' }]}
                            getValueProps={(value) => ({
                                value: value && dayjs.isDayjs(value) ? value : value ? dayjs(value) : null
                            })}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="YYYY-MM-DD HH:mm"
                                showTime
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="paymentMethod"
                    label="Payment Method"
                    rules={[{ required: true, message: 'Payment method is required' }]}
                >
                    <Select>
                        <Option value="Card">Card</Option>
                        <Option value="Payment Link">Payment Link</Option>
                        <Option value="Check Payment">Check Payment</Option>
                        <Option value="Zelle">Zelle</Option>
                        <Option value="Cashapp">Cashapp</Option>
                        <Option value="Venmo">Venmo</Option>
                        <Option value="Wire Transfer">Wire Transfer</Option>
                        <Option value="PayPal">PayPal</Option>
                        <Option value="Pay Tomorrow">Pay Tomorrow</Option>
                        <Option value="Other">Other</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: 'Status is required' }]}
                >
                    <Select>
                        <Option value="Completed">Completed</Option>
                        <Option value="Pending">Pending</Option>
                        <Option value="Failed">Failed</Option>
                        <Option value="Refunded">Refunded</Option>
                        <Option value="Cancelled">Cancelled</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Description"
                >
                    <Input.TextArea rows={2} />
                </Form.Item>

                <Form.Item
                    name="notes"
                    label="Notes"
                >
                    <Input.TextArea rows={2} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

    const renderPaymentSummary = () => {
        if (!paymentDetails) return null;

        return (
            <Card>
                {isAdmin && 
                    <Card 
                        size="small" 
                        style={{ 
                            marginBottom: '24px', 
                            backgroundColor: '#fafafa',
                            border: '1px solid #e8e8e8',
                            borderRadius: '8px'
                        }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{ minWidth: '120px' }}>
                                        <Text strong>Payment Paid:</Text>
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Tooltip title="To update payment status, please go to Pending Payments page.">
                                            <div style={{ flex: 1 }}>
                                                <Select
                                                    value={payementAllow}
                                                    style={{ width: '100%' }}
                                                    disabled
                                                    placeholder="-- Select --"
                                                >
                                                    <Option value="No">
                                                        <Space>
                                                            <Tag color="orange" style={{ marginRight: 4 }}>No</Tag>
                                                            <span>No</span>
                                                        </Space>
                                                    </Option>
                                                    <Option value="Yes">
                                                        <Space>
                                                            <Tag color="green" style={{ marginRight: 4 }}>Yes</Tag>
                                                            <span>Yes</span>
                                                        </Space>
                                                    </Option>
                                                </Select>
                                            </div>
                                        </Tooltip>
                                        {payementAllow === 'Yes' ? (
                                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                                        ) : payementAllow === 'No' ? (
                                            <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                                        ) : null}
                                    </div>
                                </div>
                            </Col>    
                        </Row>
                    </Card>
                }
                
                {/* Payment Summary Cards */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Card
                            size="small"
                            style={{
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                color: 'white',
                                borderRadius: '8px'
                            }}
                            hoverable
                        >
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total Amount</span>}
                                value={paymentDetails.totalAmount}
                                precision={2}
                                prefix="$"
                                valueStyle={{ color: 'white', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                    
                    <Col xs={24} sm={12} md={6}>
                        <Card
                            size="small"
                            style={{
                                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                color: 'white',
                                borderRadius: '8px'
                            }}
                            hoverable
                        >
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>{paymentDetails.isFullyPaid ? "Total Paid" : "Advance Paid"}</span>}
                                value={paymentDetails.paidAmount}
                                precision={2}
                                prefix="$"
                                valueStyle={{ color: 'white', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                    
                    <Col xs={24} sm={12} md={6}>
                        <Card
                            size="small"
                            style={{
                                background: paymentDetails.balanceDue > 0
                                    ? 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)'
                                    : 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                color: 'white',
                                borderRadius: '8px'
                            }}
                            hoverable
                        >
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>{paymentDetails.isFullyPaid ? "Settled" : "Balance Due"}</span>}
                                value={paymentDetails.balanceDue}
                                precision={2}
                                prefix="$"
                                valueStyle={{ color: 'white', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Card
                            size="small"
                            style={{
                                background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                                color: 'white',
                                borderRadius: '8px'
                            }}
                            hoverable
                        >
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Payment Progress</span>}
                                value={paymentDetails.totalAmount > 0
                                    ? ((paymentDetails.paidAmount / paymentDetails.totalAmount) * 100).toFixed(1)
                                    : 0}
                                suffix="%"
                                valueStyle={{ color: 'white', fontSize: '24px' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {totalAmountChange && (
                    <div style={{ marginTop: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <UserOutlined /> Total amount last changed by{' '}
                            <Text strong style={{ fontSize: '12px' }}>
                                {totalAmountChangedBy}
                            </Text>
                            {' '}on {formatPSTDate(totalAmountChange.addedAt)}
                        </Text>
                    </div>
                )}

                <Divider style={{ margin: '24px 0' }} />

{/* Payment Timeline Header with Add Payment and Edit Total Amount Buttons */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
    <Title level={4} style={{ margin: 0 }}>Payment History</Title>
    <Space size="middle">
        {/* Edit Total Amount Button */}
        <Tooltip title="Edit Total Amount">
            <Button
                type="default"
                onClick={handleEditTotalAmount}
                icon={<EditOutlined />}
                size="large"
                disabled={isEditTotalAmountDisabled}
            >
                Edit Total Amount
            </Button>
        </Tooltip>
        
        {/* Add Payment Button */}
        {!paymentDetails.isFullyPaid && (
            <Button
                type="primary"
                onClick={handleAddPayment}
                icon={<PlusOutlined />}
                size="large"
            >
                Add Payment
            </Button>
        )}
    </Space>
</div>

                {/* Payment History Timeline */}
                {!paymentDetails?.payments || paymentDetails.payments.length === 0 ? (
                    <Empty
                        description="No payment history found"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                        <Button
                            type="primary"
                            onClick={handleAddPayment}
                            icon={<PlusOutlined />}
                        >
                            Record First Payment
                        </Button>
                    </Empty>
                ) : (
                    <Timeline mode="left">
                        {sortPaymentsNewestFirst(paymentDetails.payments).map(({ payment, index }) => (
                            <Timeline.Item
                                key={index}
                                color={
                                    payment.status === 'Completed' ? 'green' :
                                        payment.status === 'Pending' ? 'orange' :
                                            payment.status === 'Failed' ? 'red' : 'gray'
                                }
                                dot={getPaymentMethodIcon(payment.paymentMethod)}
                            >
                                <Card
                                    size="small"
                                    hoverable
                                    style={{
                                        borderLeft: `4px solid ${getPaymentMethodColor(payment.paymentMethod)}`,
                                        cursor: 'pointer',
                                        marginBottom: '8px',
                                        borderRadius: '8px',
                                        transition: 'all 0.3s'
                                    }}
                                    bodyStyle={{ padding: '12px' }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }} onClick={() => showPaymentDetailsModal(payment, index)}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                                <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>
                                                    ${Number(payment.amount).toFixed(2)}
                                                </Text>
                                                <Tag
                                                    color={getPaymentMethodColor(payment.paymentMethod)}
                                                    icon={getPaymentMethodIcon(payment.paymentMethod)}
                                                >
                                                    {payment.paymentMethod}
                                                </Tag>
                                                <Tag color={getPaymentStatusColor(payment.status)}>
                                                    {payment.status}
                                                </Tag>
                                            </div>
                                            <div style={{ marginBottom: '8px' }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {formatPSTDate(payment.date)}
                                                    {payment.transactionId && ` • ID: ${payment.transactionId}`}
                                                </Text>
                                            </div>
                                            {renderPaymentActors(payment)}
                                            {payment.notes && (
                                                <div style={{
                                                    padding: '8px 12px',
                                                    backgroundColor: '#fafafa',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    color: '#666',
                                                    borderLeft: '3px solid #1890ff'
                                                }}>
                                                    📝 {payment.notes}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <Space>
                                                {/* <Tooltip title="Edit Payment">
                                                    <Button
                                                        type="link"
                                                        icon={<EditOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditPayment(payment, index);
                                                        }}
                                                    />
                                                </Tooltip> */}
                                                <Tooltip title="View Details">
                                                    <Button
                                                        type="link"
                                                        icon={<EyeOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            showPaymentDetailsModal(payment, index);
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Space>
                                        </div>
                                    </div>
                                </Card>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                )}
 
                {/* Fully Paid Alert */}
                {paymentDetails.isFullyPaid && (
                    <Alert
                        message="Payment Details Received"
                        description="This lead has been fully paid. No further payments required."
                        type="success"
                        showIcon
                        style={{ marginBottom: '24px', borderRadius: '8px', marginTop: '24px' }}
                    />
                )}

                {/* Payment Statistics */}
                <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
                    <Col xs={24} sm={8}>
                        <Card size="small" style={{ borderRadius: '8px' }}>
                            <div style={{ textAlign: 'center' }}>
                                <CheckCircleOutlined style={{ fontSize: '28px', color: '#52c41a', marginBottom: '8px' }} />
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                                    {paymentDetails.completedCount}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>Completed</div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card size="small" style={{ borderRadius: '8px' }}>
                            <div style={{ textAlign: 'center' }}>
                                <ClockCircleOutlined style={{ fontSize: '28px', color: '#fa8c16', marginBottom: '8px' }} />
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                                    {paymentDetails.pendingCount}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>Pending</div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card size="small" style={{ borderRadius: '8px' }}>
                            <div style={{ textAlign: 'center' }}>
                                <CloseCircleOutlined style={{ fontSize: '28px', color: '#ff4d4f', marginBottom: '8px' }} />
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                                    {paymentDetails.failedCount}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>Failed</div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Card>
        );
    };

    return (
        <>
            <Card style={{ borderRadius: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <Title level={3} style={{ margin: 0 }}>
                        Payment Details
                    </Title>
                </div>

                <Tabs
                    activeKey={activePaymentTab}
                    onChange={setActivePaymentTab}
                    style={{ marginBottom: '24px' }}
                >
                    <TabPane tab="Summary" key="summary" />
                </Tabs>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        {activePaymentTab === 'summary' && renderPaymentSummary()}
                    </>
                )}
            </Card>
            
            {renderEditPaymentModal()}

            {/* Edit Total Amount Modal */}
<Modal
    title="Edit Total Amount"
    open={isTotalAmountModalVisible}
    onCancel={() => setIsTotalAmountModalVisible(false)}
    footer={[
        <Button key="cancel" onClick={() => setIsTotalAmountModalVisible(false)}>
            Cancel
        </Button>,
        <Button 
            key="submit" 
            type="primary" 
            loading={savingTotalAmount}
            onClick={handleSaveTotalAmount}
        >
            Update Total Amount
        </Button>
    ]}
    width={450}
    centered
>
    <Form layout="vertical">
        <Card size="small" style={{ marginBottom: 20, background: '#f5f5f5' }}>
            <Text strong>{lead?.personalInfo?.fullName || 'N/A'}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
                {lead?.vehicleInfo?.make} {lead?.vehicleInfo?.model} - {lead?.vehicleInfo?.part}
            </Text>
            <div style={{ marginTop: 8 }}>
                <Text type="secondary">Current Paid Amount: </Text>
                <Text strong style={{ color: '#52c41a' }}>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(paymentDetails?.paidAmount || 0)}
                </Text>
            </div>
            <div style={{ marginTop: 4 }}>
                <Text type="secondary">Current Balance: </Text>
                <Text strong style={{ color: '#fa8c16' }}>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(paymentDetails?.balanceDue || 0)}
                </Text>
            </div>
        </Card>

        <Form.Item
            label="Current Total Amount"
            extra={totalAmountChange
                ? `Last changed by ${totalAmountChangedBy} on ${formatPSTDate(totalAmountChange.addedAt)}`
                : undefined}
        >
            <InputNumber
                value={paymentDetails?.totalAmount}
                disabled
                style={{ width: '100%' }}
                prefix="$"
                step={0.01}
                precision={2}
            />
        </Form.Item>

        <Form.Item
            label="New Total Amount"
            required
            extra="Updates the balance and records the change in the Re-Quoted tab under your name"
        >
            <InputNumber
                value={editingTotalAmount}
                onChange={setEditingTotalAmount}
                style={{ width: '100%' }}
                prefix="$"
                min={0.01}
                step={0.01}
                precision={2}
                placeholder="Enter new total amount"
                autoFocus
            />
        </Form.Item>

        <Alert
            message="Balance will be updated"
            description={`New balance will be: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                getBalance(editingTotalAmount, paymentDetails?.paidAmount)
            )}`}
            type="info"
            showIcon
        />
    </Form>
</Modal>
        </>
    );
};

export default PaymentDetailTab;
